import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import {
  mapBlobToConversationInsights,
  mapBlobToExecutiveSummary,
  mapBlobToGeneration,
  mapBlobToKeyMetrics,
  mapBlobToQuotesEnabled,
  mapBlobToSessionExploration,
  mapBlobToTagUserIds,
  mapReportDemographics,
  mapReportDataVersions,
  mapReportFilteredUserIds,
} from './mappers';

import type {
  NatalieReportData,
  ReportDataBlob,
  ReportDataDownload,
  ReportDataRequestArgs,
  ReportDataVersion,
  ReportRequestArgs,
  ReportTagQueryArgs,
  ReportingApiReport,
} from './types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { TagDemographic } from 'tags';

const PIN_HEADER = 'X-Report-Pin';

const getBaseUrl = () => configService.getUrl(UrlNames.ReportingApi);

const toQueryError = (status: number, data: unknown): { error: FetchBaseQueryError } => ({
  error: { status, data } as FetchBaseQueryError,
});

/**
 * Bare `fetch` for PIN-authenticated and S3 requests, deliberately bypassing `ajaxService`:
 *
 * - `ajaxService` attaches `Authorization: Bearer …`, which invalidates an S3 presigned URL's
 *   query signature.
 * - Its response interceptor calls `keycloak.logout()` on any 401. A wrong PIN on the public
 *   report page returns 401, so routing PIN requests through it would sign out a logged-in
 *   visitor who followed a shared link.
 */
const pinFetch = async <T>(path: string, pin: string | undefined, signal?: AbortSignal, data?: unknown) => {
  try {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      ...(data !== undefined ? { method: 'POST', body: JSON.stringify(data) } : {}),
      headers:
        data !== undefined
          ? { ...(pin ? { [PIN_HEADER]: pin } : {}), 'Content-Type': 'application/json' }
          : pin
            ? { [PIN_HEADER]: pin }
            : undefined,
      signal,
    });

    if (!response.ok) {
      return toQueryError(response.status, await response.text().catch(() => response.statusText));
    }

    return { data: (await response.json()) as T };
  } catch (error) {
    if (signal?.aborted) return toQueryError(499, 'Request aborted');

    return toQueryError(500, error instanceof Error ? error.message : 'Network error');
  }
};

export const reportingApi = createApi({
  reducerPath: 'reportingApi',
  tagTypes: ['REPORT', 'REPORT_DATA', 'REPORT_TAGS', 'REPORT_VERSIONS'],
  baseQuery: axiosBaseQuery({ baseURL: getBaseUrl }),
  endpoints: (builder) => ({
    getReport: builder.query<ReportingApiReport, ReportRequestArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT', id: reportId }],
      queryFn: async ({ reportId, pin }, api, _extraOptions, baseQuery) => {
        if (pin) return pinFetch<ReportingApiReport>(`/v1/reports/${reportId}`, pin, api.signal);

        return baseQuery({ url: `/v1/reports/${reportId}` }) as Promise<
          { data: ReportingApiReport } | { error: FetchBaseQueryError }
        >;
      },
    }),

    /**
     * Two hops: reporting-api hands back a presigned S3 URL, then the payload is fetched
     * straight from S3. Deliberately one endpoint rather than two so the presigned URL — valid
     * for an hour — is never itself cached; an expired signature can only be hit mid-flight,
     * and a refetch mints a fresh one.
     *
     * The blob averages ~110 kB and reaches ~1.1 MB in prod, so it is fetched once per report
     * and sliced into the three tab view models by the providers rather than re-requested.
     */
    getReportData: builder.query<NatalieReportData, ReportDataRequestArgs>({
      // `versionId` is part of the serialized args, so every version is its own cache entry.
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT_DATA', id: reportId }],
      queryFn: async ({ reportId, pin, versionId }, api, _extraOptions, baseQuery) => {
        const path = `/v1/reports/${reportId}/data`;

        const downloadResult = pin
          ? await pinFetch<ReportDataDownload>(
              versionId ? `${path}?versionId=${encodeURIComponent(versionId)}` : path,
              pin,
              api.signal
            )
          : ((await baseQuery(versionId ? { url: path, params: { versionId } } : { url: path })) as
              | { data: ReportDataDownload }
              | { error: FetchBaseQueryError });

        if ('error' in downloadResult) return downloadResult;

        const { downloadUrl, versionId: servedVersionId, lastModified, redacted } = downloadResult.data;

        if (!downloadUrl) return toQueryError(502, 'Report data response contained no download URL');

        let blob: ReportDataBlob;

        try {
          // No credentials: the presigned URL carries its own signature and S3 rejects a
          // request that also presents an Authorization header.
          const response = await fetch(downloadUrl, { signal: api.signal });

          if (!response.ok) return toQueryError(response.status, 'Failed to download report data');

          blob = (await response.json()) as ReportDataBlob;
          // The response says so even if the copy were missing its own stamp.
          if (redacted === true) blob = { ...blob, redacted: true };
        } catch (error) {
          if (api.signal?.aborted) return toQueryError(499, 'Request aborted');

          return toQueryError(502, error instanceof Error ? error.message : 'Failed to download report data');
        }

        return {
          data: {
            versionId: servedVersionId,
            lastModified,
            generation: mapBlobToGeneration(blob),
            executiveSummary: mapBlobToExecutiveSummary(blob),
            conversationInsights: mapBlobToConversationInsights(blob),
            sessionExploration: mapBlobToSessionExploration(blob),
            keyMetrics: mapBlobToKeyMetrics(blob),
            userIds: mapBlobToTagUserIds(blob),
            quotesEnabled: mapBlobToQuotesEnabled(blob),
            redacted: blob.redacted === true,
          },
        };
      },
    }),

    /**
     * Signed in by JWT, or by the share-link PIN (`pin` set), which is part of the cache key. The
     * public page reads it only to number the version it shows; it never offers choosing one.
     */
    getReportDataVersions: builder.query<ReportDataVersion[], ReportRequestArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT_VERSIONS', id: reportId }],
      queryFn: async ({ reportId, pin }, api, _extraOptions, baseQuery) => {
        const path = `/v1/reports/${reportId}/data/versions`;
        const result = pin ? await pinFetch<unknown>(path, pin, api.signal) : await baseQuery({ url: path });

        if ('error' in result) return { error: result.error as FetchBaseQueryError };

        try {
          return { data: mapReportDataVersions(result.data) };
        } catch {
          return toQueryError(502, 'Invalid report versions response');
        }
      },
    }),

    getReportDemographics: builder.query<TagDemographic[], ReportTagQueryArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT_TAGS', id: reportId }],
      queryFn: async ({ reportId, pin, ...data }, api, _extraOptions, baseQuery) => {
        const url = `/v1/reports/${reportId}/demographics`;
        const result = pin
          ? await pinFetch<unknown>(url, pin, api.signal, data)
          : await baseQuery({ url, method: 'POST', data });

        if ('error' in result) return { error: result.error };

        try {
          return { data: mapReportDemographics(result.data) };
        } catch {
          return toQueryError(502, 'Invalid report demographics response');
        }
      },
    }),

    filterReportUsers: builder.query<number[], ReportTagQueryArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT_TAGS', id: reportId }],
      queryFn: async ({ reportId, pin, ...data }, api, _extraOptions, baseQuery) => {
        const url = `/v1/reports/${reportId}/users/filter`;
        const result = pin
          ? await pinFetch<unknown>(url, pin, api.signal, data)
          : await baseQuery({ url, method: 'POST', data });

        if ('error' in result) return { error: result.error };

        try {
          return { data: mapReportFilteredUserIds(result.data, data.userIds) };
        } catch {
          return toQueryError(502, 'Invalid report tag filter response');
        }
      },
    }),

    updateReport: builder.mutation<ReportingApiReport, Pick<ReportingApiReport, 'reportId' | 'name'>>({
      invalidatesTags: (_result, _error, { reportId }) => [{ type: 'REPORT', id: reportId }],
      query: ({ reportId, name }) => ({ url: `/v1/reports/${reportId}`, method: 'PUT', data: { name } }),
    }),
  }),
});

export const {
  useGetReportQuery,
  useLazyGetReportQuery,
  useGetReportDataQuery,
  useGetReportDataVersionsQuery,
  useGetReportDemographicsQuery,
  useFilterReportUsersQuery,
  useUpdateReportMutation,
} = reportingApi;
